// AllCustomers.jsx
import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import { Link, useNavigate } from "react-router-dom";
import AdminNavbar from "../Layout/AdminNavbar";
import API_BASE_URL from "../config";

const INDUSTRIES = ["IT", "Construction", "Manufacturing", "Consulting", "Marketing", "Healthcare", "Finance", "Education", "Other"];
const CUSTOMER_TYPES = ["Individual", "Company", "Government", "NGO", "Other"];
const STATUSES = ["Active", "Inactive", "Blacklisted"];

// ── Confirmation Modal ──────────────────────────────────────────────────────
const ConfirmModal = ({ show, title, message, confirmLabel, confirmVariant = "danger", onConfirm, onCancel }) => {
  if (!show) return null;
  return (
    <>
      {/* Backdrop */}
      <div
        style={{
          position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)",
          zIndex: 1050, transition: "opacity 0.15s",
        }}
        onClick={onCancel}
      />
      {/* Dialog */}
      <div
        role="dialog"
        aria-modal="true"
        style={{
          position: "fixed", top: "50%", left: "50%",
          transform: "translate(-50%, -50%)",
          zIndex: 1055, width: "100%", maxWidth: 420,
          background: "#fff", borderRadius: 12,
          boxShadow: "0 20px 60px rgba(0,0,0,0.18)",
          padding: "28px 28px 22px",
        }}
      >
        {/* Icon + Title */}
        <div className="d-flex align-items-center gap-3 mb-3">
          <div
            style={{
              width: 44, height: 44, borderRadius: 10, flexShrink: 0,
              display: "flex", alignItems: "center", justifyContent: "center",
              background: confirmVariant === "danger" ? "#fff0f0" : "#fff8e6",
            }}
          >
            <i
              className={`bi ${confirmVariant === "danger" ? "bi-trash3" : "bi-slash-circle"}`}
              style={{
                fontSize: 20,
                color: confirmVariant === "danger" ? "#dc3545" : "#fd7e14",
              }}
            />
          </div>
          <h6 className="mb-0 fw-bold" style={{ fontSize: 16 }}>{title}</h6>
        </div>

        {/* Message */}
        <p className="text-muted mb-4" style={{ fontSize: 14, lineHeight: 1.6 }}>{message}</p>

        {/* Actions */}
        <div className="d-flex justify-content-end gap-2">
            <button
            className={`btn btn-sm btn-${confirmVariant} px-4`}
            style={{ borderRadius: 8 }}
            onClick={onConfirm}
          >
            {confirmLabel}
          </button>
          <button
            className="btn btn-sm btn-outline-secondary px-4"
            style={{ borderRadius: 8 }}
            onClick={onCancel}
          >
            Cancel
          </button>
          
        </div>
      </div>
    </>
  );
};

// ───────────────────────────────────────────────────────────────────────────
const AllCustomers = () => {
  const [customers, setCustomers] = useState([]);
  const [filteredCustomers, setFilteredCustomers] = useState([]);
  const [error, setError] = useState("");

  // Filters
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [industryFilter, setIndustryFilter] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [showFilters, setShowFilters] = useState(false);
  const [showExportMenu, setShowExportMenu] = useState(false);

  // Sorting
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });

  const [downloading, setDownloading] = useState(false);

  // ── Modal state ────────────────────────────────────────────────────────
  const [modal, setModal] = useState({
    show: false,
    type: null,          // "delete" | "deactivate"
    customer: null,
  });

  const openDeleteModal    = (customer) => setModal({ show: true, type: "delete",     customer });
  const openDeactivateModal = (customer) => setModal({ show: true, type: "deactivate", customer });
  const closeModal          = ()          => setModal({ show: false, type: null, customer: null });

  const navigate = useNavigate();
  const token = localStorage.getItem("token");

  // Bootstrap loader
  useEffect(() => {
    const bootstrapLink = document.createElement("link");
    bootstrapLink.rel = "stylesheet";
    bootstrapLink.href = "https://cdn.jsdelivr.net/npm/bootstrap@5.3.2/dist/css/bootstrap.min.css";
    document.head.appendChild(bootstrapLink);
    const iconsLink = document.createElement("link");
    iconsLink.rel = "stylesheet";
    iconsLink.href = "https://cdn.jsdelivr.net/npm/bootstrap-icons@1.11.1/font/bootstrap-icons.css";
    document.head.appendChild(iconsLink);
    return () => { bootstrapLink.remove(); iconsLink.remove(); };
  }, []);

  // Auth check
  useEffect(() => {
    const roles = JSON.parse(localStorage.getItem("roles") || "[]");
    if (!token) { toast.error("You must be logged in."); navigate("/"); return; }
    if (!roles.includes("ROLE_ADMIN") && !roles.includes("ROLE_CUSTOMER_MANAGER")) {
      toast.error("Unauthorized."); navigate("/"); return;
    }
  }, [navigate, token]);

  // Fetch customers
  useEffect(() => {
    fetch(`${API_BASE_URL}/api/customers/all`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    })
      .then((res) => { if (!res.ok) throw new Error("Failed to fetch customers"); return res.json(); })
      .then((data) => {
        const list = Array.isArray(data) ? data : data.content || [];
        setCustomers(list);
        setFilteredCustomers(list);
      })
      .catch((err) => setError(err.message));
  }, [token]);

  // Apply filters and sorting
  useEffect(() => {
    let result = [...customers];
    if (search) {
      result = result.filter(
        (c) =>
          c.customerName?.toLowerCase().includes(search.toLowerCase()) ||
          c.customerCode?.toLowerCase().includes(search.toLowerCase()) ||
          c.primaryContactEmail?.toLowerCase().includes(search.toLowerCase())
      );
    }
    if (statusFilter) result = result.filter((c) => c.status === statusFilter);
    if (industryFilter) result = result.filter((c) => c.industry === industryFilter);
    if (typeFilter) result = result.filter((c) => c.customerType === typeFilter);
    if (sortConfig.key) {
      result.sort((a, b) => {
        const valA = a[sortConfig.key] ?? "";
        const valB = b[sortConfig.key] ?? "";
        if (valA < valB) return sortConfig.direction === "asc" ? -1 : 1;
        if (valA > valB) return sortConfig.direction === "asc" ? 1 : -1;
        return 0;
      });
    }
    setFilteredCustomers(result);
  }, [search, statusFilter, industryFilter, typeFilter, customers, sortConfig]);

  const handleSort = (key) => {
    setSortConfig((prev) => ({
      key,
      direction: prev.key === key && prev.direction === "asc" ? "desc" : "asc",
    }));
  };
  const getSortArrow = (key) => {
    if (sortConfig.key !== key) return "";
    return sortConfig.direction === "asc" ? "↑" : "↓";
  };

  // ── Confirmed actions ──────────────────────────────────────────────────
  const handleConfirm = async () => {
    const { type, customer } = modal;
    closeModal();

    if (type === "delete") {
      try {
        const res = await fetch(`${API_BASE_URL}/api/customers/delete/${customer.id}`, {
          method: "DELETE",
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) throw new Error("Cannot delete — may be linked to an active project.");
        toast.success("Customer deleted.");
        setCustomers((prev) => prev.filter((c) => c.id !== customer.id));
      } catch (err) {
        toast.error(err.message || "Failed to delete customer.");
      }
    }

    if (type === "deactivate") {
      try {
        const res = await fetch(`${API_BASE_URL}/api/customers/update/${customer.id}`, {
          method: "PUT",
          headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
          body: JSON.stringify({ ...customer, status: "Inactive" }),
        });
        if (!res.ok) throw new Error();
        toast.success("Customer deactivated.");
        setCustomers((prev) =>
          prev.map((c) => c.id === customer.id ? { ...c, status: "Inactive" } : c)
        );
      } catch {
        toast.error("Failed to deactivate customer.");
      }
    }
  };



  const downloadReport = async (format) => {
    try {
      setDownloading(true);
      const params = new URLSearchParams({ format });
      if (statusFilter) params.append("status", statusFilter);
      const res = await fetch(`${API_BASE_URL}/api/reports/customers?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error();
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Customer_Report.${format === "pdf" ? "pdf" : "xlsx"}`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Report downloaded!");
      setShowExportMenu(false);
    } catch { toast.error("Failed to download report."); } finally { setDownloading(false); }
  };

  const formatDate = (iso) => iso ? new Date(iso).toLocaleDateString("en-GB") : "";

  const initials = (name = "") =>
    name.split(" ").slice(0, 2).map((w) => w[0]).join("").toUpperCase() || "??";

  const getStatusBadge = (status) => {
    const map = { Active: "success", Inactive: "secondary", Blacklisted: "danger" };
    return <span className={`badge bg-${map[status] || "secondary"}`}>{status || "—"}</span>;
  };

  const activeCount   = filteredCustomers.filter((c) => c.status === "Active").length;
  const inactiveCount = filteredCustomers.filter((c) => c.status === "Inactive").length;

  // ── Modal config derived from state ───────────────────────────────────
  const modalConfig = modal.type === "delete"
    ? {
        title: "Delete Customer",
        message: `Are you sure you want to permanently delete "${modal.customer?.customerName}"? This action cannot be undone.`,
        confirmLabel: "Delete",
        confirmVariant: "danger",
      }
    : {
        title: "Deactivate Customer",
        message: `Are you sure you want to deactivate "${modal.customer?.customerName}"? They will no longer appear as active.`,
        confirmLabel: "Deactivate",
        confirmVariant: "warning",
      };

  return (
    <>
      {/* Confirmation Modal */}
      <ConfirmModal
        show={modal.show}
        title={modalConfig.title}
        message={modalConfig.message}
        confirmLabel={modalConfig.confirmLabel}
        confirmVariant={modalConfig.confirmVariant}
        onConfirm={handleConfirm}
        onCancel={closeModal}
      />

      <div className="fixed-top">
        <AdminNavbar />
      </div>
      <div className="container-fluid my-4" style={{ paddingTop: "60px" }}></div>
      <div className="d-flex flex-column min-vh-100">
        <div className="container-fluid p-3 bg-light flex-grow-1">

          {/* Page Header */}
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h4 className="fw-bold">
              <i className="bi bi-people me-2"></i> Customers
            </h4>
            <div className="d-flex gap-2 align-items-center">
              {/* Export dropdown */}
              <div style={{ position: "relative" }}>
                <button
                  className="btn btn-sm btn-outline-secondary"
                  onClick={() => setShowExportMenu((p) => !p)}
                  disabled={downloading}
                >
                  {downloading ? "Exporting…" : "↓ Export ▾"}
                </button>
                {showExportMenu && (
                  <div
                    style={{
                      position: "absolute", top: "110%", right: 0, background: "#fff",
                      border: "1px solid #e2e2e2", borderRadius: 8,
                      boxShadow: "0 8px 20px rgba(0,0,0,0.08)", minWidth: 180, zIndex: 1000,
                    }}
                    onMouseLeave={() => setShowExportMenu(false)}
                  >
                    <div
                      className="px-3 py-2"
                      style={{ cursor: "pointer", fontSize: 13, borderBottom: "1px solid #f0f0ee" }}
                      onClick={() => downloadReport("excel")}
                    >
                      📊 Export Excel
                    </div>
                    <div
                      className="px-3 py-2"
                      style={{ cursor: "pointer", fontSize: 13 }}
                      onClick={() => downloadReport("pdf")}
                    >
                      📋 Export PDF
                    </div>
                  </div>
                )}
              </div>
              <button className="btn btn-primary btn-sm" onClick={() => navigate("/addcustomer")}>
                + New Customer
              </button>
            </div>
          </div>

          {/* Stats Bar */}
          <div className="d-flex align-items-center gap-3 mb-3 p-2 bg-white border rounded">
            <small className="fw-semibold text-muted text-uppercase" style={{ letterSpacing: "0.5px" }}>Overview</small>
            <span className="badge rounded-pill bg-light text-dark border">{filteredCustomers.length} Total</span>
            <span className="badge rounded-pill" style={{ background: "#edf5ed", color: "#356035", border: "1px solid #c8e6c9" }}>
              {activeCount} Active
            </span>
            <span className="badge rounded-pill" style={{ background: "#f5f5f3", color: "#888", border: "1px solid #e2e2e2" }}>
              {inactiveCount} Inactive
            </span>
          </div>

          {/* Collapsible Filters */}
          <div
            className="p-2 bg-light border mb-2"
            style={{ cursor: "pointer" }}
            onClick={() => setShowFilters(!showFilters)}
          >
            <strong>{showFilters ? "− Filters" : "+ Filters"}</strong>
          </div>
          {showFilters && (
            <div className="row mb-3">
              <div className="col-md-4 mb-2">
                <input
                  type="text" className="form-control"
                  placeholder="Search by name, code, or email…"
                  value={search} onChange={(e) => setSearch(e.target.value)}
                />
              </div>
              <div className="col-md-2 mb-2">
                <select className="form-select" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                  <option value="">All Statuses</option>
                  {STATUSES.map((s) => <option key={s}>{s}</option>)}
                </select>
              </div>
              <div className="col-md-2 mb-2">
                <select className="form-select" value={industryFilter} onChange={(e) => setIndustryFilter(e.target.value)}>
                  <option value="">All Industries</option>
                  {INDUSTRIES.map((i) => <option key={i}>{i}</option>)}
                </select>
              </div>
              <div className="col-md-2 mb-2">
                <select className="form-select" value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)}>
                  <option value="">All Types</option>
                  {CUSTOMER_TYPES.map((t) => <option key={t}>{t}</option>)}
                </select>
              </div>
            </div>
          )}

          {/* Toolbar */}
          <div className="d-flex justify-content-between align-items-center p-2 bg-light border mb-2">
            <div className="d-flex align-items-center gap-2">
              <span className="text-muted small">Total: {filteredCustomers.length}</span>
            </div>
          </div>

          {/* Table */}
          <div style={{ overflowX: "auto" }}>
            <table className="table table-sm align-middle text-start">
              <thead>
                <tr>
                  <th>View | Edit </th>
                  <th onClick={() => handleSort("customerCode")} style={{ cursor: "pointer" }}>
                    Code {getSortArrow("customerCode")}
                  </th>
                  <th onClick={() => handleSort("customerName")} style={{ cursor: "pointer" }}>
                    Customer Name {getSortArrow("customerName")}
                  </th>
                  <th>Type</th>
                  <th onClick={() => handleSort("industry")} style={{ cursor: "pointer" }}>
                    Industry {getSortArrow("industry")}
                  </th>
                  <th onClick={() => handleSort("status")} style={{ cursor: "pointer" }}>
                    Status {getSortArrow("status")}
                  </th>
                  <th>Currency</th>
                  <th>Payment Terms</th>
                  <th onClick={() => handleSort("createdAt")} style={{ cursor: "pointer" }}>
                    Created {getSortArrow("createdAt")}
                  </th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredCustomers.length > 0 ? (
                  filteredCustomers.map((c) => (
                    <tr key={c.id}>
                      <td>
                        <Link to={`/viewcustomer/${c.id}`} className="me-1 text-primary">View</Link>
                        |
                        <Link to={`/editcustomer/${c.id}`} className="mx-1 text-primary">Edit</Link>
                      </td>
                      <td>
                        <span style={{ background: "#eaf0f8", color: "#3a6090", borderRadius: 4, padding: "2px 8px", fontFamily: "monospace", fontSize: 12 }}>
                          {c.customerCode || "—"}
                        </span>
                      </td>
                      <td>
                        <div className="d-flex align-items-center gap-2">
                          <div
                            style={{
                              width: 30, height: 30, borderRadius: 8, flexShrink: 0,
                              background: "#eaf0f8", display: "flex", alignItems: "center",
                              justifyContent: "center", fontSize: 11, fontWeight: 600, color: "#3a6090",
                            }}
                          >
                            {initials(c.customerName)}
                          </div>
                          <div>
                            <div className="fw-normal">{c.customerName}</div>
                            {/* {(c.city || c.country) && (
                              <div className="text-muted" style={{ fontSize: 11 }}>
                                {[c.city, c.country].filter(Boolean).join(", ")}
                              </div>
                            )} */}
                          </div>
                        </div>
                      </td>
                      <td><span className="badge bg-light text-dark border">{c.customerType || "—"}</span></td>
                      <td style={{ fontSize: 12.5, color: "#777" }}>{c.industry || "—"}</td>
                      <td>{getStatusBadge(c.status)}</td>
                      <td style={{ fontFamily: "monospace", fontSize: 12, fontWeight: 500 }}>{c.currency || "—"}</td>
                      <td style={{ fontSize: 12.5 }}>{c.paymentTerms || "—"}</td>
                      <td style={{ fontFamily: "monospace", fontSize: 12 }}>{formatDate(c.createdAt)}</td>
                      <td>
                        <div className="d-flex gap-1">
                          {c.status !== "Inactive" && (
                            <button
                              className="btn btn-sm btn-outline-warning"
                              title="Deactivate"
                              onClick={() => openDeactivateModal(c)}
                            >
                              <i className="bi bi-slash-circle"></i>
                            </button>
                          )}
                          <button
                            className="btn btn-sm btn-outline-danger"
                            title="Delete"
                            onClick={() => openDeleteModal(c)}
                          >
                            <i className="bi bi-trash"></i>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="12" className="text-muted text-center py-4">No customers found.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {error && <p className="text-danger mt-3">{error}</p>}
        </div>
      </div>
    </>
  );
};

export default AllCustomers;